import { useEffect, useRef } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { useGetEventInsightsQuery } from '@/services/eventApi';
import { useNotification } from '@/context/NotificationContext';
import { createErrorMessage } from '@/utils';

/**
 * EventInsights Component
 * 
 * Displays key metrics and insights for a specific event in the admin swarm interface.
 * Fetches event data using the useGetEventInsightsQuery hook and displays metrics
 * such as tickets purchased, total sold, total margin, and venue capacity.
 * 
 * @param {Object} props
 * @param {number} props.bteId - The unique identifier for the event
 * 
 * @returns {JSX.Element} A Box component containing event metrics
 * 
 * @example
 * // Expected data shape from the API:
 * const eventData = {
 *   bteId: number,          // Event unique identifier
 *   eventName: string,      // Full name of the event
 *   ticketsPurchasedTotal: number,  // Total number of tickets purchased
 *   totalSold: number,      // Total number of tickets sold
 *   totalMargin: number,    // Total margin in dollars
 *   venueCapacity: number   // Maximum capacity of the venue
 * }
 */
export default function EventInsights({ bteId }) {
  const { data, isLoading, isError, error } = useGetEventInsightsQuery(bteId, { skip: !bteId });
  const { showNotification } = useNotification();

  // Ref to control when to show the error notification
  const hasShownError = useRef(false);

  // Show error notification when API call fails
  useEffect(() => {
    if (isError && !hasShownError.current) {
      showNotification(createErrorMessage(error), 'error');
      hasShownError.current = true;
    }
  }, [isError, error, showNotification]);
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Typography sx={{ fontSize:'14px' }}>
          <strong>Event Insights:</strong>{' '}
          Tix purchased: <Skeleton width={20} sx={{display: 'inline-block'}} />  •{' '}
          Total sold: <Skeleton width={20} sx={{display: 'inline-block'}} /> •{' '}
          Total margin: <Skeleton width={20} sx={{display: 'inline-block'}} /> •{' '}
          Venue capacity: <Skeleton width={20} sx={{display: 'inline-block'}} />
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Typography color="error" sx={{ fontSize:'14px' }}>
          <strong>Error:</strong> Unable to load event insights
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 4, flexDirection: 'row' }}>
      <Typography sx={{ fontSize:'14px',display: 'flex', flexDirection: 'row' }}>
        <strong>Event Insights:</strong>{' '}< div style={{width: '16px'}}/>
        Tix purchased: <strong> {data?.ticketsPurchasedTotal || 0} {' '}</strong> < div style={{width: '16px'}}/> 
        Total sold: <strong> {data?.totalSold || 0} {' '} </strong> < div style={{width: '16px'}}/>
        Total margin: <strong> {data?.totalMargin || 0}% {' '}  </strong> < div style={{width: '16px'}}/>
        Venue capacity: <strong> {data?.venueCapacity || 0} </strong>
      </Typography>
    </Box>
  );
}
